import React, { useEffect } from 'react';
import noop from 'lodash/noop';
import { observer } from 'mobx-react-lite';

import type { PermissionLevel } from '@atlassian/proforma-common-core/jira-common-models';
import { getDefaultFormId, useFormIndexState } from '@atlassian/proforma-form-system';
import type { ProjectType } from '@atlassian/proforma-utils/types';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useHandleFormSelected as useHandleFormSelectedWithFiFState,
	useSelectFormNoDiscardChangeNotice,
} from '../../controllers/use-handle-form-selected/index.tsx';
import type { IssueViewStore } from '../../stores/issue-view-store.tsx';
import { JiraAddForm } from './jira-add-form/index.tsx';
import { JiraFormCardIndex } from './jira-form-card-index/index.tsx';

interface IssueViewProps {
	proformaUrl: string;
	issueViewStore: IssueViewStore;
	permissions: PermissionLevel;
	showFormVisibility: boolean;
	addFormButtonClickedCount: number;
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void;
	projectType: ProjectType;
}

export const IssueView: React.FunctionComponent<IssueViewProps> = observer(
	({
		issueViewStore,
		proformaUrl,
		addFormButtonClickedCount,
		disableIssueViewFullPageDropzoneCallback,
		projectType,
	}) => {
		const handleFormSelected = useHandleFormSelectedWithFiFState(
			issueViewStore,
			disableIssueViewFullPageDropzoneCallback,
		);

		const selectFormNoDiscardChangeNotice = fg('granular_permissions_on_forms_ga')
			? useSelectFormNoDiscardChangeNotice(
					issueViewStore.loadFormStore,
					disableIssueViewFullPageDropzoneCallback,
				)
			: noop;

		const {
			formIndex,
			isInitialised: isFormIndexInitialised,
			selectedFormId,
		} = useFormIndexState();

		useEffect(() => {
			if (fg('granular_permissions_on_forms_ga')) {
				if (isFormIndexInitialised) {
					const formIdToSelect =
						selectedFormId || getDefaultFormId(formIndex, issueViewStore.projectId);
					if (formIdToSelect !== undefined) {
						const formToSelect = formIndex.find((form) => form.id === formIdToSelect);
						selectFormNoDiscardChangeNotice(formToSelect?.id, undefined, formToSelect?.hasAccess);
					}
				}
				return;
			}
			if (isFormIndexInitialised) {
				if (selectedFormId) {
					handleFormSelected(selectedFormId);
				} else {
					const defaultFormId = getDefaultFormId(formIndex, issueViewStore.projectId);
					if (defaultFormId) {
						handleFormSelected(defaultFormId);
					}
				}
			}
			// Only depend on isFormIndexInitialised so this effect only runs once after the index is initialised
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isFormIndexInitialised]);

		return (
			<div>
				<JiraFormCardIndex
					issueViewStore={issueViewStore}
					disableIssueViewFullPageDropzoneCallback={disableIssueViewFullPageDropzoneCallback}
					proformaUrl={proformaUrl}
					projectType={projectType}
				/>
				<JiraAddForm
					addFormButtonClickedCount={addFormButtonClickedCount}
					issueViewStore={issueViewStore}
					disableIssueViewFullPageDropzoneCallback={disableIssueViewFullPageDropzoneCallback}
					issueId={issueViewStore.issueId}
				/>
			</div>
		);
	},
);
